// Angular Files

import {
  Component,
  OnInit,
  OnDestroy,
  Inject,
  ViewChild,
  AfterViewInit,
  ChangeDetectorRef,
  HostBinding,
  DOCUMENT
} from '@angular/core';
import { Location } from '@angular/common';
import {
    NavigationCancel,
    NavigationCancellationCode,
    NavigationEnd,
    NavigationError,
    NavigationStart,
    Router,
    RouterEvent,
    Event,
    RoutesRecognized
} from '@angular/router';

// Angular Material Files
import { MatSidenav } from '@angular/material/sidenav';
import { MatIconRegistry } from '@angular/material/icon';

// Other External Files
import { Subscription } from 'rxjs';
import { delay, filter } from 'rxjs/operators';

// Payment Integration Files

// Teller Online Files

// Teller Online Library Files
import {
    TellerOnlineSiteMetadataService,
    TellerOnlineAppService,
    TellerOnlineFocusRestorationService,
    TellerOnlineNotificationBannerService,
    TellerOnlineNotificationBannerAction,
    TellerOnlineRailNavigationElement,
    TellerOnlineRailNavigationService,
    CartStatusEnum
} from 'teller-online-libraries/core';
import {
    TellerOnlineMessageService,
    TellerOnlineWindowService
} from 'teller-online-libraries/shared';
import { TellerOnlineIconsRegistryService } from 'teller-online-libraries/icons';
import {
    NoticeTypeEnumDto,
    TerminologyNameEnumDto
} from 'libraries/core/src/lib/api/CoreWebApi';
import {
    CartModel,
    CartService,
    UserService,
    AuthService,
    InboundRedirectService
} from './core/services';
import { PaymentMethodTypeEnum, PaymentProcessorProvider } from './payment-integrations';
import { isAccountSettingsUrl } from './core/utils/account-settings-routes';
import { isRailShowing } from './core/utils/rail.utils';
import { LOAD_ERROR_PATH } from './features/load-error/load-error.component';

// The pay flow's pages, which the rail counts as part of Online Services along with home
export const ONLINE_SERVICES_PATHS = [
    '/search',
    '/cart',
    '/checkout',
    '/download-receipt',
    '/submission-confirmation',
    '/schedule'
];

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild('cart') cart: MatSidenav;

    @ViewChild('help') help: MatSidenav;

    @ViewChild('mobilemenu') mobilemenu: MatSidenav;

    @ViewChild('accountmenu') accountmenu: MatSidenav;

    public get railShowing(): boolean {
        return isRailShowing(this.railNavigationService, this.appService);
    }

    @HostBinding('class') get getClass() {
        let classList = '';
        if (this.railShowing) {
            classList += ' app-root--rail-enabled';
        }
        if (this.isRailFillPage) {
            classList += ' app-root--rail-fill';
        }
        return classList;
    }

    /**
     * True on every /profile page, where content fills the rail-inset width instead of
     * centering. Independent of whether the rail itself is currently on screen.
     */
    public get isRailFillPage(): boolean {
        return this.appService.currentUrl.startsWith('/profile');
    }

    // Public Variables
    public cartItemCount: number = 0;

    public userMustVerifyEmail: boolean = false;

    public showVerifyLink: boolean = true;

    // Private Variables
    private _navigationState;

    private _cartVersion: number = 0;

    /** The action to perform when the verify email button is clicked from the banner */
    private _VERIFY_EMAIL_ACTION: TellerOnlineNotificationBannerAction = {
        text: 'Verify Now',
        href: '/profile/personal-info',
        hrefState: { showEmailConf: true }
    };

    // Subscriptions
    private _cartSubscription: Subscription;

    private _routerEventSubscription: Subscription;

    private _userProfileSubscription: Subscription;

    private _itemAddedSubscription: Subscription;

    private _windowOfflineSubscription: Subscription;

    private _openCartSubscription: Subscription;

    private _breakpointsSubscription: Subscription;

    private _authDetailsSubscription: Subscription;

    constructor(
        @Inject(DOCUMENT) private document: Document,
        private cdr: ChangeDetectorRef,
        private focusService: TellerOnlineFocusRestorationService,
        private messageService: TellerOnlineMessageService,
        private iconRegistry: MatIconRegistry,
        // Injected purely so Angular constructs it: its constructor registers our custom SVG
        // icons (merchant-logos, etc.) with MatIconRegistry. Without this, svgIcon won't resolve.
        private iconRegistryService: TellerOnlineIconsRegistryService,
        private location: Location,
        public notificationBannerService: TellerOnlineNotificationBannerService,
        public railNavigationService: TellerOnlineRailNavigationService,
        public paymentProvider: PaymentProcessorProvider,
        public cartService: CartService,
        public appService: TellerOnlineAppService,
        public router: Router,
        public siteMetadataService: TellerOnlineSiteMetadataService,
        public userService: UserService,
        public authService: AuthService,
        public windowService: TellerOnlineWindowService,
        public inboundRedirectService: InboundRedirectService
    ) {
        this.iconRegistry.setDefaultFontSetClass('material-symbols-rounded');

        // Navigation between pages
        this._routerEventSubscription = this.router.events.pipe(
            filter((event: Event | RouterEvent) => event instanceof RouterEvent)
        ).subscribe((event: RouterEvent) => {
            this.handleRouterEventSubscription(event);
        });

        // Listen for if the user loses network connection
        windowService.addEventListener('offline', () => {
            this.notificationBannerService.addBanner({
                tag: 'offline',
                message: 'Error contacting the server, please check your network connection.',
                bannerClass: 'notification-banner--caution',
                icon: 'warning',
                resolveAction: {
                    text: ''
                }
            });
        });

        windowService.addEventListener('online', () => {
            this.notificationBannerService.dismissBanner('offline');
        });

        windowService.addEventListener(
            'pageshow', (event: PageTransitionEvent) => this.handlePageShow(event));

        this.loadTenantStyles();
    }

    ngOnInit() {
        this._userProfileSubscription = this.userService.currentUser$.subscribe(user => {
            this.userMustVerifyEmail = (user && !user?.emailVerified) ?? false;
            if (this.userMustVerifyEmail) {
                this.notificationBannerService.addBanner({
                    tag: 'verify-email',
                    message: 'Please check your email and enter the confirmation code to verify your email address.',
                    resolveAction: {
                        text: this._VERIFY_EMAIL_ACTION.text,
                        href: this._VERIFY_EMAIL_ACTION.href,
                        hrefState: this._VERIFY_EMAIL_ACTION.hrefState
                    },
                    bannerClass: 'notification-banner--caution',
                    icon: 'warning',
                    requiresAuthentication: true
                });
            } else {
                this.notificationBannerService.dismissBanner('verify-email');
            }
        });

        this._itemAddedSubscription = this.cartService.itemAdded$.subscribe((added: boolean) => {
            // Open the cart if the item was added and the app is in inbound redirect mode with a redirect processor
            if (added && this.inboundRedirectService.isInboundRedirect && this.paymentProvider.activeCreditProcessor.redirect) {
                this.cart.open();
            }
        });

        // Feature pages (e.g. checkout's mobile "View cart") ask the shell to open the cart
        // drawer via CartService, since only the shell owns the <mat-sidenav #cart>.
        this._openCartSubscription = this.cartService.openCartRequested$.subscribe(() => {
            if (!this.cart?.opened) this.toggleCart();
        });

        // The mobile menu and account-menu drawers only exist on mobile widths (their toggles
        // disappear at wide widths), so close them if the viewport widens past the mobile
        // breakpoints while one is open — otherwise it stays stranded over the desktop layout.
        // AppService subscribes to breakpoints$ first (in its constructor), so its window-size
        // flags are already updated by the time this handler runs.
        this._breakpointsSubscription = this.appService.breakpoints$
            .subscribe(() => this.handleBreakpointChange());

        if (this.authService.isSignedIn) {
            this.userService.getUserProfile();
            this._updateRailNavigation();
        }

        // Catches sign-in and sign-out that happen without a page reload.
        this._authDetailsSubscription = this.authService.authDetailsChange
            .subscribe(() => this._updateRailNavigation());

        this.siteMetadataService.activeNotices.forEach(n => {
            this.siteMetadataService.createNoticeBanner(n);
        });

        this.siteMetadataService.upcomingNotices.forEach(n => {
            setTimeout(() => {
                this.siteMetadataService.createNoticeBanner(n);
            }, n.startTimeUtc.getTime() - new Date().getTime());
        });

        // Listen for errors triggered within the payment processors
        this.paymentProvider.activeCreditProcessor.errorEvent$.subscribe(() => {
            // If the payment processor throws an error, make sure we remove any loading state on the app
            this.appService.finishLoading();
        });

        this.paymentProvider.activeECheckProcessor.errorEvent$.subscribe(() => {
            // If the payment processor throws an error, make sure we remove any loading state on the app
            this.appService.finishLoading();
        });

        this.appService.finishLoading();
    }

    ngAfterViewInit() {
        this._cartSubscription = this.cartService.cart$.pipe(delay(0))
            .subscribe(cart => this.handleCartChange(cart));
    }

    ngOnDestroy() {
        if (this._cartSubscription) this._cartSubscription.unsubscribe();
        if (this._routerEventSubscription) this._routerEventSubscription.unsubscribe();
        if (this._userProfileSubscription) this._userProfileSubscription.unsubscribe();
        if (this._itemAddedSubscription) this._itemAddedSubscription.unsubscribe();
        if (this._openCartSubscription) this._openCartSubscription.unsubscribe();
        if (this._breakpointsSubscription) this._breakpointsSubscription.unsubscribe();
        if (this._authDetailsSubscription) this._authDetailsSubscription.unsubscribe();
    }

    loadTenantStyles() {
        const head = this.document.getElementsByTagName('head')[0];
        const tenantStylesLink = this.document.getElementById('tenant-styles') as HTMLLinkElement;

        // Same rule as the backend, and null-safe.
        if (!this.siteMetadataService?.hasServableTenant) {
            return;
        }

        // Read from the same object the guard above validated, so the checked
        // tenant and the requested one cannot diverge.
        const { tenant } = this.siteMetadataService;

        if (tenant) {
            // api/assets/* is served by the backend AssetsController from the
            // bucket root (the upload target), so tenant branding is identical
            // regardless of which build track the SPA is served from. A
            // relative assets/* path resolves to the track prefix and would
            // miss uploads made after the track was created.
            const styleName = `api/assets/${tenant}/styles.css`;

            if (tenantStylesLink) {
                tenantStylesLink.href = styleName;
            } else {
                const style = this.document.createElement('link');
                style.id = 'tenant-styles';
                style.rel = 'stylesheet';
                style.href = `${styleName}`;

                head.appendChild(style);
            }
        } else if (tenantStylesLink) {
            tenantStylesLink.remove();
        }
    }

    public toggleCart() {
        if (!this.cart.opened) {
            this.focusService.save('cart');
        }

        this.cart.toggle();
    }

    public toggleMobileMenu() {
        this.mobilemenu.toggle();
    }

    /** Close the mobile-only drawers when the viewport crosses out of the mobile widths. */
    public handleBreakpointChange() {
        // isWide is the same flag the header uses to swap the hamburger and the avatar drawer
        // button for their wide-width equivalents, so closing on it keeps the drawers alive
        // exactly as long as the buttons that open them. isMobile is not interchangeable here:
        // it stays true through the Expanded window size class, which overlaps isWide, and
        // would leave a band of widths where a drawer is open with no control to dismiss it.
        if (this.appService.isWide) {
            if (this.mobilemenu?.opened) this.mobilemenu.close();
            if (this.accountmenu?.opened) this.accountmenu.close();
        }
    }

    /**
     * QA 3.1.16: pressing Back on a redirect provider's page (e.g. NIC) can restore this page
     * from the browser's back/forward cache exactly as it was left, with the "Redirecting..."
     * overlay still up and the cart as it was before the payment locked it.
     */
    public handlePageShow(event: PageTransitionEvent) {
        if (!event.persisted) return;

        this.appService.finishLoading();
        this.appService.finishPageLoading();

        if (this.cartService.cartGuid) {
            this.cartService.refreshCart(this.cartService.cartGuid, undefined, false);
        }
    }

    public toggleAccountMenu() {
        this.accountmenu.toggle();
    }

    public async accountMenuOpening() {
        this.cart.close();
        this.help.close();
        this.mobilemenu.close();
    }

    public async toggleHelp() {
        if (!this.help.opened) {
            this.focusService.save('help');
        }

        this.help.toggle();
    }

    public async helpOpening() {
        this.cart.close();
        this.mobilemenu.close();
        this.accountmenu.close();
    }

    public helpClosed() {
        // Delay so Angular propagates help.opened=false to the RestrictFocusDirective
        // before we move focus (mirrors cartClosed). Restores focus to the element that
        // opened the help drawer (the header Help button).
        setTimeout(() => {
            this.focusService.restore('help');
        });
    }

    public async cartOpening() {
        this.mobilemenu.close();
        this.help.close();
        this.accountmenu.close();

        // Focus the cart heading so screen readers announce the cart context
        setTimeout(() => {
            const element: HTMLElement = this.document.querySelector('.cart .sidebar-header h2');
            element?.focus();
        }, 50);
    }

    public cartClosed() {
        // Delay so Angular propagates cart.opened=false to
        // the RestrictFocusDirective before we move focus
        setTimeout(() => {
            this.focusService.restore(
                'cart',
                parent =>
                    parent?.querySelector<HTMLElement>(
                        '.search-result__remove-btn'
                    )
                    ?? parent?.querySelector<HTMLElement>(
                        '[aria-controls="aria_cart"]'
                    )
            );
        });
    }

    public async menuOpening() {
        this.cart.close();
        this.help.close();
        this.accountmenu.close();
    }

    /** Keeps the cart badge in step with the cart. */
    handleCartChange(cart: CartModel) {
        this.appService.consoleLog(this, cart, `Event order: ${cart?.version}`);

        // If this is an older version of the cart, we don't want it
        if (cart?.version && cart.version < this._cartVersion) return;

        // Update the cart version
        this._cartVersion = cart?.version;

        // Ensure the cart has finished loading before doing anything
        if (!this.cartService.loadingCart) {
            if (cart && cart.cartId) {
                // Count the rows the cart lists: an invoice held twice is listed once
                // (CartService._generateItemsForDisplay), so it counts once
                this.cartItemCount = (cart.itemsForDisplay ?? cart.items)?.length;
            } else {
                this.cartItemCount = 0;
            }
        } else {
            // This is for if we are loading the cart later and need to reset the
            // cartItemCount - it should always be 0 if it's loading
            this.cartItemCount = 0;
        }
    }

    handleRouterEventSubscription(event: RouterEvent) {
        const navigationExtras = this.router.currentNavigation()?.extras;
        this._navigationState = navigationExtras?.state;
        this.appService.consoleLog(this, this._navigationState);
        this.appService.routerState = this._navigationState;

        switch (true) {
            case event instanceof RoutesRecognized: {
                // Set the routeTitle to be used later when setDocumentTitle is called

                // Walk to the deepest route that carries a title: componentless parents
                // (e.g. the inbound-redirect hydration wrapper) and feature routing modules
                // (e.g. /profile) both put the real title below root.firstChild.
                let routeTitle: string | undefined;
                for (let route = event.state?.root; route; route = route.firstChild) {
                    if (route.data?.title) {
                        routeTitle = route.data.title;
                    }
                }

                // A tenant-configurable term cannot live in static route data, so a route named
                // by one carries the term's name instead and the title resolves here — on every
                // navigation, since onSameUrlNavigation: 'reload' re-runs this for a click on
                // the page the user is already on.
                for (let route = event.state?.root; route; route = route.firstChild) {
                    if (route.data?.terminologyTitle) {
                        routeTitle = this.siteMetadataService
                            .term(route.data.terminologyTitle).plural;
                    }
                }

                this.appService.setRouteTitle(routeTitle);
                break;
            }
            case event instanceof NavigationStart: {
                // Ensure all of the sidebars are closed if they should be by default. Help is a
                // modal drawer, so it always closes on navigation
                if (this.help?.opened) this.toggleHelp();
                if (this.mobilemenu?.opened) this.toggleMobileMenu();
                if (this.accountmenu?.opened) this.toggleAccountMenu();
                if (this.cart?.opened && !(this.inboundRedirectService.isInboundRedirect && this.paymentProvider.activeCreditProcessor.redirect)) this.toggleCart();

                let loadingMessage = 'Loading...';
                switch (true) {
                    case event.url.startsWith('/checkout/success'):
                        loadingMessage = 'Retrieving Receipt...';
                        break;
                    case event.url.startsWith('/checkout/processing'):
                        loadingMessage = 'Checking Payment...';
                        break;
                    default:
                        break;
                }

                this.appService.triggerLoading(loadingMessage);

                // Navigation to the checkout page in the TOL context is a two-step process ( -> /checkout?paymentMethodType=<methodType> -> /checkout),
                // so, to avoid unnecessary API calls, we need to skip status update on the first step
                const isTOLInitialCheckOut = event.url.startsWith('/checkout') && !!navigationExtras?.queryParams?.paymentMethodType;
                // When we're on the checkout page, update the cart's status to be CheckingOut
                if (event.url.startsWith('/checkout')
                    && !event.url.includes('redirect')
                    && !event.url.includes('processing')
                    && !event.url.includes('success')
                    && !isTOLInitialCheckOut) {
                    this.cartService.updateStatus(CartStatusEnum.CheckingOut);
                }

                // Refresh cart asynchronously to improve sync between backend and frontend.
                // This shouldn't happen on
                // - checkout-processing
                // - while TOL context is loading. There are a few navigations that happen there (/TOL -> /checkout?paymentMethodType=<methodType> -> /checkout),
                //   we need to refresh the cart only on the last step
                // These are go-between pages that don't need the regular functionality.
                // Refreshing cart there is redundant or can lead to issues with updating the status of the cart.
                // All other situations are covered by the suppressCartRefresh state variable.
                // - an inbound-redirect landing: the stored guid is stale, and its response
                //   could land after InboundRedirectHydrationGuard adopts the server-built cart
                if (this.cartService.cartGuid
                    && !event.url.startsWith('/checkout/processing')
                    && !event.url.startsWith('/TOL')
                    && !isTOLInitialCheckOut
                    && !this._isInboundRedirectLanding(event.url)
                    && !this._navigationState?.suppressCartRefresh) {
                    this.cartService.refreshCart(this.cartService.cartGuid, undefined, false);
                }

                // if we're on the receipt page, close the cart because it's not relevant on this page
                if (event.url.startsWith('/checkout/success')) {
                    if (this.cart.opened) this.toggleCart();
                }
                break;
            }
            case event instanceof NavigationCancel:
                if ((event as NavigationCancel).code === NavigationCancellationCode.Redirect) {
                    break;
                }
            // falls through
            case event instanceof NavigationError:
                this.appService.finishLoading();
                this.cdr.detectChanges();

                if (event instanceof NavigationError) this._showLoadError(event);
                break;
            case event instanceof NavigationEnd: {
                let loadedMessage = 'Loaded';

                // Update the title attribute for the page
                this.appService.setDocumentTitle();

                // Redisplay any active notice alerts
                this.siteMetadataService.activeNotices.forEach(n => {
                    if (n.noticeType === NoticeTypeEnumDto.Alert && !this.notificationBannerService.banners.find(b => b.message === n.message)) this.siteMetadataService.createNoticeBanner(n);
                });

                switch (true) {
                    case event.url.startsWith('/checkout/success'):
                        loadedMessage = 'Retrieved receipt';
                        break;
                    case event.url.startsWith('/checkout/processing'):
                        loadedMessage = '';
                        break;
                    default:
                        break;
                }

                this.appService.finishLoading(loadedMessage);

                this.appService.currentUrl = event.urlAfterRedirects;

                this._updateRailNavigation();

                // If there is a cartError in the state parameter, display it to the user
                if (this._navigationState && this._navigationState.cartError) {
                    this.appService.consoleLog(this, this._navigationState.cartError);
                    setTimeout(() => {
                        this.messageService.notification(this._navigationState.cartError, 'error', 6000);
                        this._forgetCartError();
                    }, 10);
                }

                // When the user is not on the checkout page their payment method is unknown, so reset
                // the payment method type to undetermined in order to use the default convenience fee config
                if (this.router.url !== '/checkout' && this.paymentProvider.defaultConfig.convenienceFeeType !== PaymentMethodTypeEnum.Undetermined) {
                    this.paymentProvider.useConvenienceFee(PaymentMethodTypeEnum.Undetermined);
                }

                // If the notification banner service is enabled, we likely have a verify email banner
                // So determine if we need to change the action of the verify email button based on what page we're on
                if (this.notificationBannerService.enabled) {
                    let verifyResolveAction;

                    if (!event.url.endsWith('/profile') && !event.url.startsWith('/profile/personal-info')) {
                        // ensure we have an anchor tag, no click handler
                        verifyResolveAction = {
                            href: this._VERIFY_EMAIL_ACTION.href,
                            hrefState: this._VERIFY_EMAIL_ACTION.hrefState,
                            click: null
                        };
                    } else {
                        // ensure we have a click handler, but not anchor tag
                        verifyResolveAction = {
                            href: null,
                            hrefState: null,
                            click: this.notificationBannerService.clickResolve
                        };
                    }

                    this.notificationBannerService.updateBanner('verify-email', {
                        resolveAction: verifyResolveAction
                    });
                }

                // push out when we focus to ensure the html has actually rendered
                setTimeout(() => {
                    const mainFocus: HTMLElement | null = this.document.querySelector('#main-content-focus');

                    if (mainFocus) {
                        mainFocus.focus();
                    }
                }, 0);

                this.cdr.detectChanges();

                if (this._shouldReinitializeInboundRedirect(event.url)) {
                    // Make sure this event happens only once per page load
                    this.inboundRedirectService.reinitializeRequest();
                }

                break;
            }
            default:
                break;
        }
    }

    /**
     * A TOL session left in localStorage is resumed once per page load (browser refresh).
     * Not on the payment-completion pages, which resume it themselves, and not on an
     * inbound-redirect landing: InboundRedirectHydrationGuard hydrates that session from the
     * redirect, and replaying an abandoned session's keys would send the user to /TOL/{old}.
     */
    private _shouldReinitializeInboundRedirect(url: string): boolean {
        return this.inboundRedirectService.isInboundRedirect
            && !this.inboundRedirectService.requestTriggered
            && !url.startsWith('/checkout/processing')
            && !url.startsWith('/checkout/success')
            && !this._isInboundRedirectLanding(url);
    }

    /**
     * The urls a server-side inbound redirect (HandleRedirect) sends the browser to: the
     * checkout/cart landing, and the error landing, which rebuilds the session itself. A
     * stale session resumed here would navigate away before the error is shown.
     */
    private _isInboundRedirectLanding(url: string): boolean {
        return url.includes('fromInboundRedirect=true')
            || url.startsWith('/inbound-redirect/error');
    }

    // The router copies history.state back into the navigation on a reload, so a cart error
    // left in it is shown again on every refresh (QA 3.1.16).
    private _forgetCartError() {
        const { cartError, ...state } = (this.location.getState() ?? {}) as Record<string, unknown>;

        if (cartError === undefined) return;

        this.location.replaceState(this.location.path(true), '', state);
    }

    /**
     * A resolver threw (a 500, a dropped connection) or a lazy chunk failed to load, which left
     * a blank page with no way forward. Show the load-error page instead, keeping the address
     * bar on the page the user asked for so a refresh retries it. A 401 is left to the error
     * handler, which sends the user to sign in.
     */
    private _showLoadError(event: NavigationError) {
        const status = event.error?.status ?? event.error?.rejection?.status;

        if (status === 401 || event.url.startsWith(LOAD_ERROR_PATH)) return;

        this.router.navigateByUrl(LOAD_ERROR_PATH, {
            skipLocationChange: true,
            state: { failedUrl: event.url }
        });
    }

    private _updateRailNavigation() {
        if (!this.authService.isSignedIn) {
            this.railNavigationService.removeNavigation();
            return;
        }

        this.railNavigationService.addNavigation(
            isAccountSettingsUrl(this.appService.currentUrl)
                ? this._buildAccountSettingsRailElements()
                : this._buildMainRailElements()
        );
    }

    private _buildAccountSettingsRailElements(): TellerOnlineRailNavigationElement[] {
        return [
            {
                icon: 'person',
                link: '/profile/personal-info',
                text: 'Personal Info'
            },
            {
                icon: 'notifications',
                link: '/profile/notification-settings',
                text: 'Notification Settings'
            },
            {
                icon: 'lock',
                link: '/profile/change-password',
                text: 'Change Password'
            }
        ];
    }

    private _buildMainRailElements(): TellerOnlineRailNavigationElement[] {
        const elements: TellerOnlineRailNavigationElement[] = [];

        elements.push({
            icon: 'category_search',
            link: '/',
            text: 'Online Services',
            exactMatch: true,
            alsoActiveOn: ONLINE_SERVICES_PATHS
        });

        if (this.siteMetadataService.linkedAccountsEnabled) {
            const linkedAccountsLabel = this.siteMetadataService
                .term(TerminologyNameEnumDto.LinkedAccount).plural;

            elements.push({
                icon: 'hub',
                link: '/profile/linked-accounts',
                text: linkedAccountsLabel,
                // The rail wraps a multi-word label onto two lines and sizes the item
                // for them, so a one-word tenant term must declare itself single-line to keep
                // the item heights aligned.
                singleLine: !linkedAccountsLabel.includes(' ')
            });
        }

        if (this.paymentProvider.canSavePaymentMethod) {
            elements.push({
                icon: 'account_balance_wallet',
                link: '/profile/payment-methods',
                text: 'Payment Methods'
            });
        }

        const paymentHistoryLabel = this.siteMetadataService.scheduledPaymentsEnabled
            ? 'Payments'
            : 'Payment History';

        elements.push({
            icon: 'receipt_long',
            link: '/profile/payment-history',
            text: paymentHistoryLabel,
            singleLine: this.siteMetadataService.scheduledPaymentsEnabled
        });

        if (this.siteMetadataService.scheduledPaymentsEnabled) {
            elements.push({
                icon: 'event',
                link: '/profile/schedules',
                text: 'Schedules',
                singleLine: true
            });
        }

        return elements;
    }
}
